import { type Level, LEVELS } from '@arrow/puzzle-schema';

import { addDays, bestStreak, currentStreak, type DateKey, type DayRecord, isDateKey, type PlayerStats, weekday } from './daily.ts';

/**
 * The player's history, read for the Stats screen: the week and month calendars, each
 * level's records and trend, Flow records and help used. Pure: today's date is passed
 * in, nothing reads a clock.
 */

/** One daily solved, in date order. */
export interface Solve {
	dateKey: DateKey;
	level: Level;
	number: number;
	elapsedMs: number;
	checksUsed: number;
	revealsUsed: number;
	bestFlow: number;
	/** Finished on its own day (counts for the streak); false for an archive or catch-up play. */
	onTime: boolean;
}

/**
 * How a level went on a day: `solved` on the day itself, `late` (finished on a later day:
 * archive, never counted for the streak), `started` and not finished, or `none`.
 */
export type LevelMark = 'solved' | 'late' | 'started' | 'none';

export interface DayCell {
	dateKey: DateKey;
	/** 1–31. */
	day: number;
	/** 0 = Monday … 6 = Sunday. */
	weekday: number;
	marks: Record<Level, LevelMark>;
	/** Any level was finished on the day: it counts for the streak. */
	counted: boolean;
	/** Levels finished (on the day or later). */
	solved: number;
	isToday: boolean;
	isFuture: boolean;
}

const isDone = (d: DayRecord | undefined): d is DayRecord => d !== undefined && d.completedAt !== null;

function markOf(day: DayRecord | undefined, dateKey: DateKey): LevelMark {
	if (!day) return 'none';
	if (day.completedAt === null) return 'started';
	return day.completedOnDay === dateKey ? 'solved' : 'late';
}

/** The calendar cell for one date. */
export function dayCell(stats: PlayerStats, dateKey: DateKey, todayKey: DateKey): DayCell {
	const levels = stats.days[dateKey] ?? {};
	const marks = Object.fromEntries(LEVELS.map((l) => [l, markOf(levels[l], dateKey)])) as Record<Level, LevelMark>;
	return {
		dateKey,
		day: Number(dateKey.slice(8, 10)),
		weekday: weekday(dateKey),
		marks,
		counted: LEVELS.some((l) => marks[l] === 'solved'),
		solved: LEVELS.filter((l) => marks[l] === 'solved' || marks[l] === 'late').length,
		isToday: dateKey === todayKey,
		isFuture: dateKey > todayKey,
	};
}

/** Today's week, Monday to Sunday (the week Sparrow's art direction ramps through). */
export function weekStrip(stats: PlayerStats, todayKey: DateKey): DayCell[] {
	if (!isDateKey(todayKey)) return [];
	const monday = addDays(todayKey, -weekday(todayKey));
	return Array.from({ length: 7 }, (_, i) => dayCell(stats, addDays(monday, i), todayKey));
}

export interface MonthView {
	year: number;
	/** 1–12. */
	month: number;
	/** Rows of seven, Monday first; `null` pads the days outside the month. */
	weeks: Array<Array<DayCell | null>>;
	/** Days with any level finished. */
	daysPlayed: number;
	/** Levels finished across the month. */
	solved: number;
}

const pad = (n: number) => String(n).padStart(2, '0');

/** A month's calendar. An invalid month gives an empty view. */
export function monthView(stats: PlayerStats, year: number, month: number, todayKey: DateKey): MonthView {
	const first = `${String(year).padStart(4, '0')}-${pad(month)}-01`;
	if (!isDateKey(first)) return { year, month, weeks: [], daysPlayed: 0, solved: 0 };
	const cells: Array<DayCell | null> = Array.from({ length: weekday(first) }, () => null);
	for (let key = first; key.slice(0, 7) === first.slice(0, 7); key = addDays(key, 1)) cells.push(dayCell(stats, key, todayKey));
	while (cells.length % 7) cells.push(null);
	const weeks: Array<Array<DayCell | null>> = [];
	for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
	const days = cells.filter((c): c is DayCell => c !== null);
	return { year, month, weeks, daysPlayed: days.filter((c) => c.solved > 0).length, solved: days.reduce((n, c) => n + c.solved, 0) };
}

/** `{ year, month }` stepped by `n` months. */
export function shiftMonth(ym: { year: number; month: number }, n: number): { year: number; month: number } {
	const i = ym.year * 12 + (ym.month - 1) + n;
	return { year: Math.floor(i / 12), month: (i % 12) + 1 };
}

/** The earliest date with any daily record (started or finished), or null. */
export function firstPlayed(stats: PlayerStats): DateKey | null {
	const keys = Object.keys(stats.days).filter(isDateKey).sort();
	return keys[0] ?? null;
}

/** Every finished daily, oldest first (easy before medium before hard on a day); one level only if given. */
export function solveHistory(stats: PlayerStats, level?: Level): Solve[] {
	const out: Solve[] = [];
	for (const dateKey of Object.keys(stats.days).filter(isDateKey).sort()) {
		for (const l of LEVELS) {
			if (level && l !== level) continue;
			const d = stats.days[dateKey]?.[l];
			if (!isDone(d)) continue;
			out.push({
				dateKey,
				level: l,
				number: d.number,
				elapsedMs: d.elapsedMs,
				checksUsed: d.checksUsed,
				revealsUsed: d.revealsUsed,
				bestFlow: d.bestFlow,
				onTime: d.completedOnDay === dateKey,
			});
		}
	}
	return out;
}

/** The median of `values` (the mean of the middle two for an even count), or null. */
export function median(values: readonly number[]): number | null {
	if (values.length === 0) return null;
	const s = [...values].sort((a, b) => a - b);
	const mid = s.length >> 1;
	return s.length % 2 ? s[mid]! : Math.round((s[mid - 1]! + s[mid]!) / 2);
}

export interface Trend {
	/** Solves in each half compared. */
	window: number;
	/** Median of the latest `window` times. */
	recentMs: number;
	/** Median of the `window` before those. */
	earlierMs: number;
	/** recent − earlier: negative is quicker. */
	deltaMs: number;
}

/**
 * Are you getting quicker? The median of your latest `window` (default 5) times against
 * the median of the ones before them. Medians, so one slow morning doesn't swing it.
 * Needs at least four times (two against two); with fewer than `2 × window`, each half
 * is half of what there is. Null when there aren't enough.
 */
export function timeTrend(times: readonly number[], window = 5): Trend | null {
	const w = Math.min(window, Math.floor(times.length / 2));
	if (w < 2) return null;
	const recentMs = median(times.slice(-w))!;
	const earlierMs = median(times.slice(-2 * w, -w))!;
	return { window: w, recentMs, earlierMs, deltaMs: recentMs - earlierMs };
}

export interface LevelRecord {
	level: Level;
	solved: number;
	/** Fastest solve on its own day with no reveals (a revealed answer isn't a solve), and its date. */
	bestMs: number | null;
	bestOn: DateKey | null;
	/** Mean and median over the solves on their own day (archive catch-ups leave them be). */
	averageMs: number | null;
	medianMs: number | null;
	/** Solved with no checks and no reveals. */
	perfect: number;
	/** The longest Flow at this level, and its date (the first time, on a tie). */
	bestFlow: number;
	bestFlowOn: DateKey | null;
	/** The latest solve at this level. */
	latest: Solve | null;
	trend: Trend | null;
}

/** One level's records, from its finished dailies. */
export function levelRecord(stats: PlayerStats, level: Level): LevelRecord {
	const all = solveHistory(stats, level);
	// Times only from solves on their own day: an archive catch-up isn't a race.
	const timed = all.filter((s) => s.onTime);
	const clean = timed.filter((s) => s.revealsUsed === 0);
	let best: Solve | null = null;
	for (const s of clean) if (!best || s.elapsedMs < best.elapsedMs) best = s;
	let flow: Solve | null = null;
	for (const s of all) if (s.bestFlow > 0 && (!flow || s.bestFlow > flow.bestFlow)) flow = s;
	const times = timed.map((s) => s.elapsedMs);
	return {
		level,
		solved: all.length,
		bestMs: best?.elapsedMs ?? null,
		bestOn: best?.dateKey ?? null,
		averageMs: times.length ? Math.round(times.reduce((a, b) => a + b, 0) / times.length) : null,
		medianMs: median(times),
		perfect: all.filter((s) => s.checksUsed === 0 && s.revealsUsed === 0).length,
		bestFlow: flow?.bestFlow ?? 0,
		bestFlowOn: flow?.dateKey ?? null,
		latest: all.at(-1) ?? null,
		// Only clean times on the day say how fast you are: a reveal stops the clock early.
		trend: timeTrend(clean.map((s) => s.elapsedMs)),
	};
}

export interface FlowRecords {
	/** The longest Flow on any daily (0 with none), where and when (the first time, on a tie). */
	best: number;
	bestOn: DateKey | null;
	bestLevel: Level | null;
	/** The median of each solve's best Flow. */
	typical: number | null;
	/** Solves by their best Flow: `[flow, count]`, ascending, from 2 (a Flow needs two answers). */
	spread: Array<[number, number]>;
	/** Solves with no Flow of two or more. */
	none: number;
}

/** Flow over every finished daily. */
export function flowRecords(stats: PlayerStats): FlowRecords {
	const all = solveHistory(stats);
	let best: Solve | null = null;
	for (const s of all) if (s.bestFlow > 0 && (!best || s.bestFlow > best.bestFlow)) best = s;
	const counts = new Map<number, number>();
	for (const s of all) if (s.bestFlow >= 2) counts.set(s.bestFlow, (counts.get(s.bestFlow) ?? 0) + 1);
	return {
		best: best?.bestFlow ?? 0,
		bestOn: best?.dateKey ?? null,
		bestLevel: best?.level ?? null,
		typical: median(all.map((s) => s.bestFlow)),
		spread: [...counts.entries()].sort((a, b) => a[0] - b[0]),
		none: all.filter((s) => s.bestFlow < 2).length,
	};
}

export interface StreakStatus {
	current: number;
	best: number;
	/** Today's daily is in (some level finished today). */
	todayCounted: boolean;
	/** The current run is the best run (and is more than a day). */
	isBest: boolean;
}

export function streakStatus(stats: PlayerStats, todayKey: DateKey): StreakStatus {
	const current = currentStreak(stats, todayKey);
	const best = bestStreak(stats);
	const todayCounted = isDateKey(todayKey) && dayCell(stats, todayKey, todayKey).counted;
	return { current, best, todayCounted, isBest: current > 1 && current >= best };
}
